import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import {
  ANNOTATION_PENDING_LIMIT_COUNT_FF,
  REALLOCATION_ORDERED_MOVABLE_COUNT_FF,
  isActiveRow,
  rowHasError,
} from './reallocation.utils';
import { WIZARD_BATCH_ORDER, WIZARD_STEPS, type ReassignmentRowState } from './types';
import { useGetReallocationAllocationDetailQuery } from '../../api/adminProjectsApiSlice';
import { useEditProject } from '../../context/EditProjectContext';
import type { ReallocationOperation } from '../../context/editProjectReducer';
import type {
  AnnotatorBatchBreakdown,
  BatchAllocationDetail,
  ReallocationBatchKey,
} from '../../types/response';

interface UseReallocationWizardArgs {
  open: boolean;
  projectId: string;
}

type RowsByBatch = Partial<Record<ReallocationBatchKey, ReassignmentRowState[]>>;

export const useReallocationWizard = ({ open, projectId }: UseReallocationWizardArgs) => {
  const { data, isLoading, isFetching } = useGetReallocationAllocationDetailQuery(
    { projectId },
    { skip: !open || !projectId }
  );

  const { addedAnnotators, confirmedReallocations } = useEditProject();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedBatches, setSelectedBatches] = useState<Set<ReallocationBatchKey>>(new Set());
  const [rowsByBatch, setRowsByBatch] = useState<RowsByBatch>({});
  const rowIdRef = useRef(0);

  const makeRow = useCallback(
    (): ReassignmentRowState => ({
      id: `row-${(rowIdRef.current += 1)}`,
      fromAnnotatorId: '',
      toAnnotatorId: '',
      count: null,
      movable: null,
    }),
    []
  );

  // (Re)opening the wizard re-seeds it from any already-confirmed reallocations
  // so prior (unsaved) moves are restored and editable — and not double-counted.
  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    rowIdRef.current = 0;

    if (confirmedReallocations.length === 0) {
      setSelectedBatches(new Set());
      setRowsByBatch({});
      return;
    }

    const seededRows: RowsByBatch = {};
    const seededBatches = new Set<ReallocationBatchKey>();
    confirmedReallocations.forEach((op) => {
      seededBatches.add(op.batch);
      (seededRows[op.batch] ??= []).push({
        id: `row-${(rowIdRef.current += 1)}`,
        fromAnnotatorId: op.fromAnnotatorId,
        toAnnotatorId: op.toAnnotatorId,
        count: op.count,
        movable: null,
      });
    });
    setSelectedBatches(seededBatches);
    setRowsByBatch(seededRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Annotators added (but not yet saved) participate as reassignment targets.
  const addedAnnotatorEntries = useMemo<AnnotatorBatchBreakdown[]>(
    () =>
      addedAnnotators.map((a) => ({
        annotatorId: a.id,
        annotatorName: a.label,
        pendingCount: 0,
        draftCount: 0,
        finalisedCount: 0,
        isUnsaved: true,
      })),
    [addedAnnotators]
  );

  // Order the batches for display; only those returned by the backend are shown.
  const batches = useMemo<BatchAllocationDetail[]>(() => {
    const byKey = new Map((data?.data?.batches ?? []).map((b) => [b.batch, b]));
    return WIZARD_BATCH_ORDER.map((key) => byKey.get(key)).filter((b): b is BatchAllocationDetail =>
      Boolean(b)
    );
  }, [data]);

  const toggleBatch = useCallback((batch: ReallocationBatchKey) => {
    setSelectedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batch)) next.delete(batch);
      else next.add(batch);
      return next;
    });
  }, []);

  const selectedBatchDetails = useMemo(
    () =>
      batches
        .filter((b) => selectedBatches.has(b.batch))
        .map((b) => ({ ...b, perAnnotator: [...b.perAnnotator, ...addedAnnotatorEntries] })),
    [batches, selectedBatches, addedAnnotatorEntries]
  );

  const goNext = useCallback(() => {
    setActiveStep((step) => {
      // Entering Configure: ensure each selected batch starts with one empty row.
      if (step === 0) {
        setRowsByBatch((prev) => {
          const next = { ...prev };
          selectedBatches.forEach((batch) => {
            if (!next[batch]?.length) next[batch] = [makeRow()];
          });
          return next;
        });
      }
      return Math.min(step + 1, WIZARD_STEPS.length - 1);
    });
  }, [selectedBatches, makeRow]);

  const goBack = useCallback(() => setActiveStep((s) => Math.max(s - 1, 0)), []);

  const addRow = useCallback(
    (batch: ReallocationBatchKey) =>
      setRowsByBatch((prev) => ({ ...prev, [batch]: [...(prev[batch] ?? []), makeRow()] })),
    [makeRow]
  );

  const updateRow = useCallback(
    (batch: ReallocationBatchKey, rowId: string, patch: Partial<ReassignmentRowState>) =>
      setRowsByBatch((prev) => ({
        ...prev,
        [batch]: (prev[batch] ?? []).map((r) => {
          if (r.id !== rowId) return r;
          const updated = { ...r, ...patch };
          // Changing From/To invalidates the cached movable cap until refetched.
          if (
            (patch.fromAnnotatorId !== undefined && patch.fromAnnotatorId !== r.fromAnnotatorId) ||
            (patch.toAnnotatorId !== undefined && patch.toAnnotatorId !== r.toAnnotatorId)
          ) {
            if (patch.movable === undefined) updated.movable = null;
          }
          return updated;
        }),
      })),
    []
  );

  const removeRow = useCallback(
    (batch: ReallocationBatchKey, rowId: string) =>
      setRowsByBatch((prev) => ({
        ...prev,
        [batch]: (prev[batch] ?? []).filter((r) => r.id !== rowId),
      })),
    []
  );

  // The committed reallocation operations across all selected batches.
  const operations = useMemo<ReallocationOperation[]>(
    () =>
      selectedBatchDetails.flatMap((batch) =>
        (rowsByBatch[batch.batch] ?? []).filter(isActiveRow).map((row) => ({
          id: row.id,
          batch: batch.batch,
          fromAnnotatorId: row.fromAnnotatorId,
          toAnnotatorId: row.toAnnotatorId,
          count: row.count as number,
        }))
      ),
    [selectedBatchDetails, rowsByBatch]
  );

  const orderedFixEnabled = Boolean(useFeatureFlagSplit(REALLOCATION_ORDERED_MOVABLE_COUNT_FF));
  const pendingLimitFixEnabled = Boolean(useFeatureFlagSplit(ANNOTATION_PENDING_LIMIT_COUNT_FF));

  // Configure is valid when no row in any selected batch is in an error state.
  const configureValid = useMemo(
    () =>
      selectedBatchDetails.every((batch) => {
        const rows = rowsByBatch[batch.batch] ?? [];
        return rows.every(
          (row) =>
            !rowHasError(rows, row, batch.perAnnotator, orderedFixEnabled, pendingLimitFixEnabled)
        );
      }),
    [selectedBatchDetails, rowsByBatch, orderedFixEnabled, pendingLimitFixEnabled]
  );

  return {
    isLoading: isLoading || isFetching,
    batches,
    activeStep,
    selectedBatches,
    selectedBatchDetails,
    rowsByBatch,
    canProceedFromSelect: selectedBatches.size > 0,
    configureValid,
    operations,
    toggleBatch,
    goNext,
    goBack,
    addRow,
    updateRow,
    removeRow,
    projectId,
  };
};
